import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Converts a date-only value to noon UTC to avoid timezone shifting issues.
 *
 * Problem: When a user selects "Nov 5, 2025" as a service date, if we send
 * "2025-11-05T00:00:00.000Z" (midnight UTC), it may display as "Nov 4" in
 * PST timezone (UTC-8), because midnight UTC = 4pm PST the previous day.
 *
 * Solution: Send dates as noon UTC (12:00:00Z). This ensures the date stays
 * the same in all timezones between UTC-12 and UTC+12.
 *
 * @param date - The date to convert (Date, Dayjs, or string)
 * @returns ISO string with time set to 12:00:00.000Z
 */
export function convertDateToNoonUTC(date: Date | Dayjs | string | null | undefined): string | undefined {
  if (!date) return undefined;

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return undefined;

  // Set time to noon UTC (12:00:00.000Z)
  return dayjsDate
    .utc()
    .hour(12)
    .minute(0)
    .second(0)
    .millisecond(0)
    .toISOString();
}

/**
 * Converts a datetime value from local timezone to UTC.
 * Used for time fields (startDatetime, endDatetime) where the time matters.
 *
 * @param datetime - The datetime to convert
 * @returns ISO string in UTC
 */
export function convertDateTimeToUTC(datetime: Date | Dayjs | string | null | undefined): string | undefined {
  if (!datetime) return undefined;

  const dayjsDate = dayjs(datetime);
  if (!dayjsDate.isValid()) return undefined;

  // Convert local time to UTC
  return dayjsDate.utc().toISOString();
}

/**
 * Combines a service date with a time value.
 *
 * @param serviceDate - The date from the service date picker
 * @param timeValue - The time from the time picker or quick select
 * @returns Combined datetime as ISO string in UTC
 */
export function combineDateAndTime(
  serviceDate: Date | Dayjs | string | null | undefined,
  timeValue: Date | Dayjs | string | null | undefined
): string | undefined {
  if (!serviceDate || !timeValue) return undefined;

  const date = dayjs(serviceDate);
  const time = dayjs(timeValue);

  if (!date.isValid() || !time.isValid()) return undefined;

  // Combine the date from serviceDate with the time from timeValue
  const combined = date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0);

  // Convert to UTC
  return combined.utc().toISOString();
}

/**
 * Prepares service ticket dates for API submission.
 * Converts date-only fields to noon UTC and datetime fields to proper UTC.
 *
 * @param values - Form values from the service ticket form
 * @returns Transformed values ready for API submission
 */
export function prepareServiceTicketDatesForAPI(values: any): any {
  const transformed = { ...values };

  // Date-only fields - send as noon UTC to avoid timezone shifts
  if (transformed.serviceDatetime) {
    transformed.serviceDatetime = convertDateToNoonUTC(transformed.serviceDatetime);
  }

  if (transformed.dueDate) {
    transformed.dueDate = convertDateToNoonUTC(transformed.dueDate);
  }

  // DateTime fields - combine with service date if needed, convert to UTC
  if (transformed.startDatetime && transformed.serviceDatetime) {
    // If startDatetime is just a time, combine it with serviceDatetime
    const startTime = dayjs(transformed.startDatetime);
    const serviceDate = dayjs(transformed.serviceDatetime);

    // Check if startDatetime has a different date than serviceDatetime
    // If dates match, it's already combined; if not, combine them
    if (startTime.format('YYYY-MM-DD') !== serviceDate.format('YYYY-MM-DD')) {
      transformed.startDatetime = combineDateAndTime(
        transformed.serviceDatetime,
        transformed.startDatetime
      );
    } else {
      transformed.startDatetime = convertDateTimeToUTC(transformed.startDatetime);
    }
  }

  if (transformed.endDatetime && transformed.serviceDatetime) {
    // If endDatetime is just a time, combine it with serviceDatetime
    const endTime = dayjs(transformed.endDatetime);
    const serviceDate = dayjs(transformed.serviceDatetime);

    if (endTime.format('YYYY-MM-DD') !== serviceDate.format('YYYY-MM-DD')) {
      transformed.endDatetime = combineDateAndTime(
        transformed.serviceDatetime,
        transformed.endDatetime
      );
    } else {
      transformed.endDatetime = convertDateTimeToUTC(transformed.endDatetime);
    }
  }

  // Other datetime fields
  if (transformed.timeIn) {
    transformed.timeIn = convertDateTimeToUTC(transformed.timeIn);
  }

  if (transformed.timeOut) {
    transformed.timeOut = convertDateTimeToUTC(transformed.timeOut);
  }

  if (transformed.completedAt) {
    transformed.completedAt = convertDateTimeToUTC(transformed.completedAt);
  }

  return transformed;
}

/**
 * Displays a date from the API in the user's local timezone.
 *
 * @param date - Date from API (should be in UTC)
 * @param format - Optional format string (default: "MMM D, YYYY")
 * @returns Formatted date string in local timezone
 */
export function displayDate(
  date: Date | Dayjs | string | null | undefined,
  format: string = "MMM D, YYYY"
): string {
  if (!date) return "";

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "Invalid date";

  // Display in local timezone
  return dayjsDate.local().format(format);
}

/**
 * Displays a datetime from the API in the user's local timezone.
 *
 * @param datetime - Datetime from API (should be in UTC)
 * @param format - Optional format string (default: "MMM D, YYYY h:mm A")
 * @returns Formatted datetime string in local timezone
 */
export function displayDateTime(
  datetime: Date | Dayjs | string | null | undefined,
  format: string = "MMM D, YYYY h:mm A"
): string {
  if (!datetime) return "";

  const dayjsDate = dayjs(datetime);
  if (!dayjsDate.isValid()) return "Invalid datetime";

  // Display in local timezone
  return dayjsDate.local().format(format);
}
